// City ambience: a "set" of beds + scene-synced one-shot events per scene, crossfaded on scene switches.
// Events are keyed on the scene loop: Δ = mod(ev.t − time() mod 240, 240); an event is scheduled once per loop cycle
// when Δ enters the horizon, at audioTimeIn(ac, Δ) so it is HEARD at ev.t (thunder ≈ 1 s after the flash).
// Every sound (a bed, or an event type) goes through its own strip, whose gain is the listener's trim for it.
import { WEATHERS, STORM_EVENTS } from '../engine/weather.js';
import { LOOP, rng, clamp } from '../engine/core.js';
import { powerCurve, createRegistry, hold, drops } from './dsp.js';
import { audioTimeIn } from './context.js';
import { BEDS, EVENTS, SOUND_NAMES, BED_MAX } from './sounds.js';

const TRIM_MAX = 2;                         // a trim scales a sound 0..2x the level its city gave it
const trimOf = v => (Number.isFinite(v) ? clamp(v, 0, TRIM_MAX) : 1);
/* At most FADING_MAX sets fade out at once. Switching once a second (browsing the city picker) never has more than four
   fading, the oldest in its silent last 0.2 s, so the cap only acts on faster switching: then the oldest fade makes room,
   its strips gliding to 0 with τ RETIRE_TAU (−70 dB after 8τ) before it's killed. Unbounded, key repeat (~300 city and
   weather changes in 16 s) piled up ~250 fading sets (~12,000 nodes), the render thread fell behind (the context clock
   at 0.1x real time) and the kills, which wait on that clock, crawled. */
const FADING_MAX = 4, RETIRE_TAU = 0.03;

/** The sounds a scene plays, in mixer order: its beds (scene order, level > 0), then its event types (first use). */
export function soundsOf(scene) {
  const out = [], add = (id, kind) => { if (!out.some(s => s.id === id)) out.push({ id, kind, name: SOUND_NAMES[id] || id }); };
  for (const [id, lv] of Object.entries((scene && scene.ambience) || {})) if (BEDS[id] && lv > 0) add(id, 'bed');
  for (const e of (scene && scene.events) || []) if (e && EVENTS[e.type] && Number.isFinite(e.t)) add(e.type, 'event');
  return out;
}

/* The weather a visitor picks (the Weather chip, site/js/engine/weather.js) changes how the city sounds: rain brings the rain
   bed in (at least RAIN_LEVEL); drizzle softens rain and wind, storms add heavier rain and scheduled thunder.
   The dry picks (snow, leaves, clear) take it out, and snow and leaves bring a little wind
   (at least WIND_LEVEL). The city's own weather, or an unknown pick, is the scene itself. The result also carries
   `weather`, for the track titles (makeTitle). One object per scene and pick, so picking again changes nothing. */
const RAIN_LEVEL = 0.45, WIND_LEVEL = 0.3;
const PICKS = WEATHERS.filter(w => w !== 'city');
const weathered = new WeakMap();                  // scene → Map(pick → scene as it sounds in that weather)
export function weatherScene(scene, weather) {
  if (!scene || !PICKS.includes(weather)) return scene;
  if (!weathered.has(scene)) weathered.set(scene, new Map());
  const picks = weathered.get(scene);
  if (!picks.has(weather)) {
    const ambience = { ...scene.ambience };
    if (weather === 'drizzle') { ambience.rain = 0.18; ambience.wind = Math.min(ambience.wind || 0, 0.12); }
    else if (weather === 'thunderstorm') { ambience.rain = 0.75; ambience.wind = Math.max(ambience.wind || 0, 0.45); }
    else if (weather === 'rain') ambience.rain = Math.max(ambience.rain || 0, RAIN_LEVEL);
    else delete ambience.rain;
    if (weather === 'snow' || weather === 'leaves') ambience.wind = Math.max(ambience.wind || 0, WIND_LEVEL);
    // Explicit picks replace native thunder (Sydney); unrelated city events and mixer trims stay intact.
    const original = scene.events || [];
    const quiet = original.some(e => e.type === 'thunder') ? original.filter(e => e.type !== 'thunder') : original;
    const events = weather === 'thunderstorm' ? [...quiet, ...STORM_EVENTS].sort((a, b) => a.t - b.t) : quiet;
    picks.set(weather, { ...scene, ambience, events, weather });
  }
  return picks.get(weather);
}

/* Heard from inside. A scene that says `indoor: true` (a room behind a wall and a shut window, as in
   site/js/scenes/tokyo-room.js) hears every sound of its set through the wall: low-passed (four poles at ROOM.hz) and
   lower (x ROOM.gain), its street echo lower still (x ROOM.echo). What stays crisp is the rain on the pane itself: the
   drops loop once more, slowed and thinned to the taps of single drops (ROOM.pane), at the rain's own level. The wall
   stands between the strips and the set's fades, so a sound's trim, the ambience volume and mute do what they do
   outdoors: the room scales what the listener set and sets nothing itself. A scene without `indoor` gets the very
   graph it always had: nothing here is made, and no random number drawn, for it. */
export const ROOM = { hz: 1000, gain: 0.7, echo: 0.4, pane: { rates: [0.71, 0.56], hp: 700, lp: 3600, gain: 0.62 } };
const FLAT_Q = -3.01;                                    // a low-pass's Q is its resonance in dB: this one has none (Butterworth)
function roomWall(ac, to, echoTo) {
  const nodes = [], lp = () => { const f = ac.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = ROOM.hz; f.Q.value = FLAT_Q; nodes.push(f); return f; };
  const G = v => { const g = ac.createGain(); g.gain.value = v; nodes.push(g); return g; };
  const dry = lp(), dry2 = lp(), level = G(ROOM.gain), echo = lp(), echoLevel = G(ROOM.gain * ROOM.echo);
  dry.connect(dry2); dry2.connect(level); level.connect(to);
  echo.connect(echoLevel); echoLevel.connect(echoTo);
  return { dry, echo, nodes };
}
/** rain on the window pane, for a room: BEDS' shape ({ name, advance, stop }); `out` carries the rain's trim */
function panePatter(ac, out, env, level, t0) {
  const { rates, hp, lp, gain } = ROOM.pane, src = [];
  const high = ac.createBiquadFilter(), low = ac.createBiquadFilter(), g = ac.createGain();
  high.type = 'highpass'; high.frequency.value = hp; high.Q.value = FLAT_Q;
  low.type = 'lowpass'; low.frequency.value = lp; low.Q.value = FLAT_Q;
  g.gain.value = gain * clamp(level, 0, 1) * BED_MAX.rain;
  rates.forEach((rate, i) => {                           // two passes over the drops, out of step, each slower than the bed's
    const s = ac.createBufferSource();
    s.buffer = drops(ac); s.loop = true; s.playbackRate.value = rate;
    s.connect(high); s.start(t0, i * 0.37 * s.buffer.duration);
    src.push(s);
  });
  high.connect(low); low.connect(g); g.connect(out);
  const voice = env.registry.add({ src, all: [...src, high, low, g], start: t0, end: Infinity, tag: `${env.tag}:bed` });
  return { name: 'pane', advance() {}, stop(t) { for (const s of src) { try { s.stop(t); } catch { /* not started */ } } voice.end = t; } };
}
/** the gains a sound's trim sets: its strip, its echo send and, in a room, the rain's patter on the pane */
const trimmed = strip => (strip.pane ? [strip.out.gain, strip.echo.gain, strip.pane.gain] : [strip.out.gain, strip.echo.gain]);

const told = new Set();
function unknown(kind, name, sceneId) {
  const k = `${kind}:${name}`;
  if (told.has(k)) return;
  told.add(k);
  console.info(`[audio] ignoring unknown ambience ${kind} "${name}" (scene "${sceneId}")`);
}
const mod = (a, n) => ((a % n) + n) % n;
/** Accepts an unbounded clock (preferred) but also survives one that wraps at 240 s. */
function unwrap(time) {
  let last = null, off = 0;
  return () => {
    const v = +time() || 0;
    if (last !== null && v < last - LOOP / 2) off += LOOP;
    last = v;
    return v + off;
  };
}

/**
 * createAmbience(ac, mixer, { key, registry, seed, trims }) →
 *   { setScene(scene, time, fade = 3), setTrim(id, v), advance(until), resync(), stats(), dispose() }
 * trims: { [sound id]: 0..TRIM_MAX } (1 = as the city was designed); they apply to every scene that has the sound.
 */
export function createAmbience(ac, mixer, { key = () => null, registry = createRegistry(), seed = 1, trims: initialTrims = {} } = {}) {
  const r = rng(seed >>> 0), trims = { ...initialTrims };
  let set = null, leaving = [], jobs = [], n = 0, time = () => ac.currentTime, fired = 0;

  function makeSet(scene, fade) {
    const now = ac.currentTime, tag = `amb${++n}`;
    const fadeIn = ac.createGain(), fadeOut = ac.createGain();
    fadeIn.connect(fadeOut); fadeOut.connect(mixer.ambIn);
    if (fade > 0) { fadeIn.gain.value = 0; fadeIn.gain.cancelScheduledValues(now); fadeIn.gain.setValueCurveAtTime(powerCurve(true), now, fade); }
    const amb = (scene && scene.ambience) || {};
    const env = { registry, tag, key, echo: mixer.echoIn, r, raining: (amb.rain || 0) > 0.05 };
    // a strip per sound: the trim on its way into the set, and the same trim on its street echo send
    const strips = new Map(), all = [fadeIn, fadeOut];
    const room = scene && scene.indoor ? roomWall(ac, fadeIn, mixer.echoIn) : null;   // heard from inside: through the wall
    if (room) all.push(...room.nodes);
    for (const { id } of soundsOf(scene)) {
      const out = ac.createGain(), echo = ac.createGain();
      out.gain.value = echo.gain.value = trimOf(trims[id]);
      out.connect(room ? room.dry : fadeIn); echo.connect(room ? room.echo : mixer.echoIn);
      all.push(out, echo);
      strips.set(id, { out, echo, env: { ...env, echo } });
    }
    const busVoice = registry.add({ src: [], all, tag: `${tag}:bus` });
    const beds = [];
    for (const [name, lv] of Object.entries(amb)) {
      if (!BEDS[name]) { unknown('bed', name, scene.id); continue; }
      if (lv > 0) { const s = strips.get(name); beds.push(BEDS[name](ac, s.out, s.env, lv, now)); }
    }
    if (room && strips.has('rain')) {                     // the rain on the pane, past the wall, under the rain's trim
      const s = strips.get('rain');
      s.pane = ac.createGain();
      s.pane.gain.value = trimOf(trims.rain);
      s.pane.connect(fadeIn);
      all.push(s.pane);
      beds.push(panePatter(ac, s.pane, env, amb.rain, now));
    }
    const events = ((scene && scene.events) || []).filter(e => {
      if (!e || !EVENTS[e.type]) { unknown('event', e && e.type, scene.id); return false; }
      return Number.isFinite(e.t);
    });
    // cycles[i]: the loop cycle event i was last scheduled for (the scene clock only runs forward, so no older one comes round again)
    return { scene, tag, strips, fadeIn, fadeOut, busVoice, beds, events, cycles: [], killAt: Infinity };
  }
  function leave(s, fade) {
    const now = ac.currentTime, f = Math.max(0.05, fade);
    s.fadeOut.gain.cancelScheduledValues(now);
    s.fadeOut.gain.setValueCurveAtTime(powerCurve(false), now, f);
    s.killAt = now + f + 0.2;
    leaving.push(s);                                              // oldest first
    while (leaving.length > FADING_MAX) retire(leaving.shift());
  }
  /** the oldest fade makes room: its strips (voices and echo sends; no curve runs on them) glide to 0, then it's killed */
  function retire(s) {
    const now = ac.currentTime;
    for (const strip of s.strips.values()) for (const p of trimmed(strip)) { hold(p, now); p.setTargetAtTime(0, now, RETIRE_TAU); }
    kill(s, now + 8 * RETIRE_TAU);
  }
  /** stop a set that is silent from t on, and free its bus */
  function kill(s, t = ac.currentTime) {
    for (const b of s.beds) b.stop(t + 0.02);
    registry.stopTag(`${s.tag}:ev`, t, 0.02);
    s.busVoice.end = t + 0.3;
    jobs.push({ at: t + 0.3, fn: () => registry.free(s.busVoice) });
  }
  function fire(s, ev, when, offset) {
    fired++;
    const strip = s.strips.get(ev.type);
    EVENTS[ev.type](ac, strip.out, Math.max(when, ac.currentTime), offset > 0 ? { ...ev, offset } : ev, strip.env);
  }
  /** fade event voices out, forget the cycles, and work the schedule out again (joins long events part-way) */
  function resync() {
    if (!set) return;
    const now = ac.currentTime;
    registry.stopTag(`${set.tag}:ev`, now, 0.1);
    set.cycles = [];
    const T = time(), lt = mod(T, LOOP);
    set.events.forEach((ev, i) => {
      const back = mod(lt - ev.t, LOOP);                       // seconds since its latest occurrence
      const joinable = ev.dur > 0 ? back < ev.dur - 0.1 : back <= 0.5;
      if (!joinable) return;
      set.cycles[i] = Math.round((T - back - ev.t) / LOOP);
      // Brief cues catch up from their attack; only explicitly long events support a sliced envelope.
      // Backdating a cue on a fresh AudioContext can otherwise schedule negative AudioParam times.
      fire(set, ev, now + 0.02, ev.dur > 0 ? back : 0);
    });
  }

  return {
    setScene(scene, timeFn, fade = 3) {
      if (set && scene === set.scene) {                          // same scene again: only a new clock needs a resync
        if (typeof timeFn === 'function' && timeFn !== set.timeFn) { time = unwrap(timeFn); set.timeFn = timeFn; resync(); }
        return;
      }
      if (typeof timeFn === 'function') time = unwrap(timeFn);
      if (set) leave(set, fade);
      set = scene ? makeSet(scene, fade) : null;
      if (set) set.timeFn = timeFn;
      resync();
    },
    /** trim one sound (τ 50 ms), in the current set and in any set still fading out (not one retired to 0) */
    setTrim(id, v) {
      trims[id] = v;
      const now = ac.currentTime, g = trimOf(v);
      for (const s of [set, ...leaving]) {
        const strip = s && s.strips.get(id);
        if (strip) for (const p of trimmed(strip)) { hold(p, now); p.setTargetAtTime(g, now, 0.05); }
      }
    },
    advance(until) {
      const now = ac.currentTime, horizon = until - now;
      if (jobs.length) { const due = jobs.filter(j => now >= j.at); jobs = jobs.filter(j => now < j.at); due.forEach(j => j.fn()); }
      for (const s of leaving) {
        if (now >= s.killAt) kill(s);
        else for (const b of s.beds) b.advance(until, now);
      }
      leaving = leaving.filter(s => now < s.killAt);
      if (!set) return;
      for (const b of set.beds) b.advance(until, now);
      if (!set.events.length) return;
      const T = time(), lt = mod(T, LOOP);
      set.events.forEach((ev, i) => {
        const d = mod(ev.t - lt, LOOP);
        if (d > horizon) return;
        const c = Math.round((T + d - ev.t) / LOOP);            // the loop cycle of this occurrence
        if (set.cycles[i] === c) return;
        set.cycles[i] = c;
        fire(set, ev, audioTimeIn(ac, d), 0);
      });
    },
    resync,
    stats: () => ({
      scene: set && set.scene && set.scene.id, indoor: !!(set && set.scene && set.scene.indoor),
      beds: set ? set.beds.map(b => b.name) : [], events: set ? set.events.length : 0,
      sets: (set ? 1 : 0) + leaving.length, fired, eventVoices: set ? registry.count(`${set.tag}:ev`) : 0,
      trims: set ? Object.fromEntries([...set.strips.keys()].map(id => [id, trimOf(trims[id])])) : {},
    }),
    dispose() {
      if (set) { leave(set, 0.3); set = null; }
      setTimeout(() => { for (const s of leaving) kill(s); leaving = []; }, 600);
    },
  };
}
